import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

/**
 * Outcome of an attempt to acquire the device location.
 * - "available"   coordinates were obtained.
 * - "denied"      permission is explicitly blocked; the system will not
 *                 re-prompt. The user must re-enable it in browser/app settings.
 * - "unavailable" permission is not blocked, but no position could be obtained
 *                 (e.g. device Location Services turned off, or a timeout).
 */
export type LocationAccessStatus = "available" | "denied" | "unavailable";

@Injectable({
  providedIn: "root",
})
export class GeolocationService {
  async requestPermissionOnAppStart(): Promise<void> {
    try {
      if (Capacitor.getPlatform() === "web") {
        if (typeof navigator === "undefined" || !navigator.geolocation) {
          return;
        }
        await new Promise<void>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            () => resolve(),
            () => resolve(),
            {
              enableHighAccuracy: false,
              timeout: 10000,
              maximumAge: 0,
            },
          );
        });
        return;
      }

      const permissions = await Geolocation.checkPermissions();
      const locationPermission =
        permissions?.location ?? permissions?.coarseLocation;

      if (
        locationPermission === "prompt" ||
        locationPermission === "prompt-with-rationale"
      ) {
        await Geolocation.requestPermissions();
      }
    } catch {
      // Keep startup non-blocking.
    }
  }

  /**
   * Prompts for location permission when it has not yet been decided, then
   * reports whether a location is available and, if not, why. Used to gate
   * features that require an active location service so the UI can show the
   * appropriate guidance.
   */
  async getLocationAccessStatus(): Promise<LocationAccessStatus> {
    if (Capacitor.getPlatform() === "web") {
      return this.getBrowserLocationStatus();
    }
    return this.getNativeLocationStatus();
  }

  private async getNativeLocationStatus(): Promise<LocationAccessStatus> {
    try {
      const permissions = await Geolocation.checkPermissions();
      let locationPermission =
        permissions?.location ?? permissions?.coarseLocation;

      if (locationPermission === "denied") {
        return "denied";
      }

      if (
        locationPermission === "prompt" ||
        locationPermission === "prompt-with-rationale"
      ) {
        const requested = await Geolocation.requestPermissions();
        locationPermission = requested?.location ?? requested?.coarseLocation;
        if (locationPermission !== "granted") {
          return "denied";
        }
      }

      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });

      const latitude = position?.coords?.latitude;
      const longitude = position?.coords?.longitude;
      return typeof latitude === "number" && typeof longitude === "number"
        ? "available"
        : "unavailable";
    } catch {
      // Permission is granted/promptable but a fix could not be obtained,
      // typically because device Location Services are off.
      return "unavailable";
    }
  }

  private async getBrowserLocationStatus(): Promise<LocationAccessStatus> {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return "unavailable";
    }

    // Always call the browser API: when the permission is undecided this opens
    // the native permission popup. When the site is already blocked the call
    // fails instantly with PERMISSION_DENIED (the browser refuses to re-prompt),
    // which we surface as "denied".
    return new Promise<LocationAccessStatus>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve("available"),
        (error) =>
          resolve(
            error.code === error.PERMISSION_DENIED ? "denied" : "unavailable",
          ),
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    });
  }

  async getCurrentGeoLocation(): Promise<string | null> {
    const nativeLocation = await this.getNativeGeoLocation();
    if (nativeLocation) {
      return nativeLocation;
    }

    return this.getBrowserGeoLocation();
  }

  private async getNativeGeoLocation(): Promise<string | null> {
    if (Capacitor.getPlatform() === "web") {
      return null;
    }

    try {
      const permissions = await Geolocation.checkPermissions();
      const locationPermission =
        permissions?.location ?? permissions?.coarseLocation;

      if (locationPermission === "denied") {
        return null;
      }

      if (
        locationPermission === "prompt" ||
        locationPermission === "prompt-with-rationale"
      ) {
        const requested = await Geolocation.requestPermissions();
        const requestedLocation =
          requested?.location ?? requested?.coarseLocation;
        if (requestedLocation !== "granted") {
          return null;
        }
      }

      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });

      const latitude = position?.coords?.latitude;
      const longitude = position?.coords?.longitude;

      if (typeof latitude !== "number" || typeof longitude !== "number") {
        return null;
      }

      return `${longitude},${latitude}`;
    } catch {
      return null;
    }
  }

  private async getBrowserGeoLocation(): Promise<string | null> {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return null;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => resolve(`${coords.longitude},${coords.latitude}`),
        () => resolve(null),
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    });
  }
}
